import { ToastController } from "@ionic/angular/standalone";
import { ErrorParseUtil } from "src/app/utils/Error";
import { onError } from "@apollo/client/link/error";
import { EErrorCode } from "@core-interfaces/global/error";
import { Preferences } from '@capacitor/preferences';
import { StorageKeys } from '@core-interfaces/global';
import { Injector } from "@angular/core";
import { Router } from "@angular/router";
import { FetchResult, NextLink, Observable, Operation } from "@apollo/client/core";
import { getMainDefinition } from "@apollo/client/utilities";
import { AuthService } from "@infra-adapters/services/auth.service";

let lastNetworkToastAt = 0;
let isHandlingInvalidToken = false;

const SESSION_EXPIRED_MESSAGE =
  'Sesión expirada, vuelve a iniciar tu sesión para seguir navegando';

const NETWORK_TOAST_COOLDOWN_MS = 15000;
const CRITICAL_OPERATION_KEYWORDS = [
  'login',
  'auth',
  'payment',
  'checkout',
  'subscription',
  'reserve',
  'booking',
  'appointment',
];

export function createGraphQLErrorLink(injector: Injector) {

  return onError(({ graphQLErrors, networkError, operation, forward, response }) => {
    const toastController = injector.get(ToastController, null);
    const router = injector.get(Router, null);

    if (graphQLErrors) {
      const parsedError = ErrorParseUtil.parseErrorResponse(graphQLErrors as any);
      const firstError = parsedError.firstError;

      if (isInvalidOrExpiredTokenError(firstError)) {
        const retry = retryWithRenewedToken(injector, operation, forward, { response, networkError });
        if (retry) {
          return retry;
        }
        void handleInvalidToken(router, toastController);
        return;
      }

      handleGraphQLErrors(graphQLErrors as any[], toastController, router);
    }

    if (networkError) {
      if (isUnauthorizedNetworkError(networkError)) {
        const retry = retryWithRenewedToken(injector, operation, forward, { response, networkError });
        if (retry) {
          return retry;
        }
        void handleInvalidToken(router, toastController);
        return;
      }
      handleNetworkError(networkError, toastController, operation?.operationName);
    }
    return;
  });
}

// Un token vencido no cierra la sesión si todavía se puede renovar. Las
// consultas se repiten con el token nuevo; las mutaciones no: el BFF vuelve a
// verificar el token después de escribir (createDiary agenda en Flowww y luego
// llama al core), así que un rechazo puede llegar con la escritura hecha, y
// repetirla duplicaría la hora. En una mutación se renueva, para que el
// siguiente intento funcione, y se entrega el error original.
//
// onError no vuelve a pasar el resultado del reintento por el handler (por eso
// no puede haber un segundo reintento), así que sus errores se tratan aquí con
// los mismos handlers; sin eso, un reintento rechazado no cerraría la sesión.
// En Apollo 3.14 la teardown de onError no desuscribe el reintento
// (link/error/index.js), así que una consulta puede repetirse aunque quien la
// pidió ya no esté. Solo se repiten consultas, que no escriben.
function retryWithRenewedToken(
  injector: Injector,
  operation: Operation,
  forward: NextLink,
  original: { response?: FetchResult; networkError?: unknown },
): Observable<FetchResult> | undefined {
  // Nunca sobre la renovación misma: esperaría su propia respuesta y quedaría
  // colgada.
  if (operation.operationName === 'RefreshToken') {
    return undefined;
  }
  const authService = injector.get(AuthService, null);
  if (!authService) {
    return undefined;
  }
  const toastController = injector.get(ToastController, null);
  const router = injector.get(Router, null);
  const definition = getMainDefinition(operation.query);
  const isQuery = definition.kind === 'OperationDefinition' && definition.operation === 'query';

  return new Observable<FetchResult>((observer) => {
    let retriedSub: { unsubscribe(): void } | undefined;
    const passOriginal = () => {
      if (original.response) {
        observer.next(original.response);
        observer.complete();
      } else {
        observer.error(original.networkError);
      }
    };

    authService.refreshTokenOnce().then(
      () => {
        if (!isQuery) {
          passOriginal();
          return;
        }
        retriedSub = forward(operation).subscribe({
          next: (result) => {
            if (result.errors) {
              void handleGraphQLErrors(result.errors as any[], toastController, router);
            }
            observer.next(result);
          },
          error: (error) => {
            if (isUnauthorizedNetworkError(error)) {
              void handleInvalidToken(router, toastController);
            } else {
              void handleNetworkError(error, toastController, operation.operationName);
            }
            observer.error(error);
          },
          complete: () => observer.complete(),
        });
      },
      () => {
        void handleInvalidToken(router, toastController);
        passOriginal();
      },
    );
    return () => retriedSub?.unsubscribe();
  });
}

const NOT_SHOW_TOAST_ERROR_CODES = [
  EErrorCode.CALENDAR_AUTH_REQUIRED,
  EErrorCode.PAYMENT_FAILED,
  EErrorCode.AUTH_INVALID_TOKEN,
  EErrorCode.NO_PREFERRED_CLINICS,
  // Los muestran en línea las pantallas de login y código: el mensaje del
  // backend está redactado para leerse completo, no para un toast de 3 s.
  EErrorCode.SIGNUP_IDENTITY_UNVERIFIED,
  EErrorCode.ACCOUNT_DISABLED,
] as string[];

async function handleGraphQLErrors(
  graphQLErrors: any[],
  toastController: ToastController | null,
  router: Router | null,
) {
  const parsedError = ErrorParseUtil.parseErrorResponse(graphQLErrors);

  if (!parsedError.hasErrors) return;

  const firstError = parsedError.firstError!;
  const isInvalidToken = isInvalidOrExpiredTokenError(firstError);
  console.log({ firstError, isInvalidToken });
  if (isInvalidToken) {
    await handleInvalidToken(router, toastController);
    return;
  }

  if (NOT_SHOW_TOAST_ERROR_CODES.includes(firstError.errorCode!)) {
    return;
  }

  await showErrorToast(
    firstError.message,
    firstError.statusCode,
    toastController
  );
}


async function handleNetworkError(
  networkError: any,
  toastController: ToastController | null,
  operationName?: string,
) {
  const diagnostic = getNetworkErrorDiagnostic(networkError);

  if (diagnostic.isAbort) {
    console.warn('[GraphQLErrorLink] Request abortado', {
      operationName,
      diagnostic,
    });
    return;
  }

  console.error('[GraphQLErrorLink] Network error detectado', {
    operationName,
    diagnostic,
    rawError: {
      name: networkError?.name,
      message: networkError?.message,
      statusCode: networkError?.statusCode,
      result: networkError?.result,
    },
  });

  const isCriticalOperation = hasCriticalOperation(operationName);
  const shouldShowToast =
    diagnostic.category !== 'unknown' ||
    isCriticalOperation;

  if (!shouldShowToast) {
    console.warn('[GraphQLErrorLink] Toast omitido por error no crítico', {
      operationName,
      category: diagnostic.category,
    });
    return;
  }

  const now = Date.now();
  if (now - lastNetworkToastAt < NETWORK_TOAST_COOLDOWN_MS) {
    console.warn('[GraphQLErrorLink] Toast omitido por cooldown', {
      operationName,
      cooldownMs: NETWORK_TOAST_COOLDOWN_MS,
    });
    return;
  }

  lastNetworkToastAt = now;
  await showErrorToast(diagnostic.message, diagnostic.statusCode, toastController);
}

function getNetworkErrorDiagnostic(networkError: any): {
  statusCode?: number;
  message: string;
  isAbort: boolean;
  category: 'offline' | 'server-unreachable' | 'server-5xx' | 'timeout' | 'security' | 'unknown';
} {
  const statusCode =
    typeof networkError?.statusCode === 'number'
      ? networkError.statusCode
      : typeof networkError?.response?.status === 'number'
        ? networkError.response.status
        : undefined;

  const name = String(networkError?.name || '').toLowerCase();
  const rawMessage = String(networkError?.message || '').toLowerCase();
  const resultString = JSON.stringify(networkError?.result || {}).toLowerCase();
  const fullMessage = `${name} ${rawMessage} ${resultString}`;

  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const isAbort =
    name.includes('abort') ||
    rawMessage.includes('abort') ||
    rawMessage.includes('cancel') ||
    rawMessage.includes('canceled');
  const isTimeout = rawMessage.includes('timeout') || rawMessage.includes('timed out');
  const isFetchFailed =
    rawMessage.includes('failed to fetch') ||
    rawMessage.includes('network request failed') ||
    rawMessage.includes('load failed') ||
    rawMessage.includes('fetch failed');

  if (isOffline) {
    return {
      statusCode,
      message: 'Sin conexión a internet. Revisa tu red e inténtalo nuevamente.',
      isAbort,
      category: 'offline',
    };
  }

  if (statusCode === 0 || isFetchFailed) {
    return {
      statusCode,
      message: 'No se pudo establecer conexión con el servidor. Intenta nuevamente.',
      isAbort,
      category: 'server-unreachable',
    };
  }

  if (statusCode && statusCode >= 500) {
    return {
      statusCode,
      message: 'El servidor no está disponible en este momento. Intenta en unos minutos.',
      isAbort,
      category: 'server-5xx',
    };
  }

  if (isTimeout) {
    return {
      statusCode,
      message: 'La solicitud tardó demasiado. Intenta nuevamente.',
      isAbort,
      category: 'timeout',
    };
  }

  if (fullMessage.includes('cors') || fullMessage.includes('ssl') || fullMessage.includes('certificate')) {
    return {
      statusCode,
      message: 'No se pudo completar la conexión de forma segura. Intenta nuevamente.',
      isAbort,
      category: 'security',
    };
  }

  return {
    statusCode,
    message: 'Ocurrió un problema de conexión temporal. Intenta nuevamente.',
    isAbort,
    category: 'unknown',
  };
}

function hasCriticalOperation(operationName?: string): boolean {
  if (!operationName) {
    return false;
  }

  const normalizedOperation = operationName.toLowerCase();
  return CRITICAL_OPERATION_KEYWORDS.some((keyword) =>
    normalizedOperation.includes(keyword)
  );
}

function isInvalidOrExpiredTokenError(
  error?: {
    errorCode?: string;
    statusCode?: number;
    message?: string;
  }
): boolean {
  if (!error) {
    return false;
  }

  if (error.errorCode === EErrorCode.AUTH_INVALID_TOKEN) {
    return true;
  }

  if (error.statusCode !== 401) {
    return false;
  }

  const normalizedMessage = (error.message || '').toLowerCase();
  const authTokenPatterns = [
    'token inválido',
    'token invalido',
    'token expirado',
    'jwt expired',
    'sesión desactualizada',
    'sesion desactualizada',
    'encabezado de autorización',
    'encabezado de autorizacion',
  ];

  return authTokenPatterns.some((pattern) =>
    normalizedMessage.includes(pattern)
  );
}

function isUnauthorizedNetworkError(networkError: any): boolean {
  const statusCode =
    typeof networkError?.statusCode === 'number'
      ? networkError.statusCode
      : typeof networkError?.response?.status === 'number'
        ? networkError.response.status
        : undefined;

  return statusCode === 401;
}


export async function showErrorToast(
  message: string,
  statusCode: number | undefined,
  toastController: ToastController | null
) {
  if (!toastController) {
    console.error('[GraphQLErrorLink] ToastController no disponible. Mensaje:', message);
    return;
  }

  let cssClass = 'app-toast error';
  if (statusCode) {
    switch (statusCode) {
      case 409:
        cssClass = 'app-toast';
        break;
      case 400:
        cssClass = 'app-toast error';
        break;
      case 401:
        cssClass = 'app-toast error';
        break;
      case 500:
      case 502:
      case 503:
        cssClass = 'app-toast error';
        break;
      default:
        cssClass = 'app-toast error';
        break;
    }
  }

  const toast = await toastController.create({
    message: message,
    duration: 3000,
    cssClass: cssClass,
    position: 'top',
  });

  await toast.present();
}

async function handleInvalidToken(
  router: Router | null,
  toastController: ToastController | null = null
): Promise<void> {
  if (isHandlingInvalidToken) {
    return;
  }
  isHandlingInvalidToken = true;

  try {
    await showSessionExpiredToast(toastController);

    await Preferences.remove({ key: StorageKeys.session_token });
    await Preferences.remove({ key: StorageKeys.refresh_token });

    if (router) {
      await router.navigateByUrl('/login', { replaceUrl: true });
    } else {
      window.location.href = '/login';
    }
  } catch (error) {
    console.error('Error handling invalid token:', error);
  } finally {
    setTimeout(() => {
      isHandlingInvalidToken = false;
    }, 3000);
  }
}

async function showSessionExpiredToast(
  toastController: ToastController | null
): Promise<void> {
  if (!toastController) {
    console.warn('[GraphQLErrorLink] ToastController no disponible para mensaje de sesión expirada.');
    return;
  }
  const toast = await toastController.create({
    message: SESSION_EXPIRED_MESSAGE,
    duration: 4000,
    cssClass: 'app-toast error',
    position: 'top',
  });
  await toast.present();
}
